/**
 * ActivityFeed widget -- Timeline of recent events with colored dots.
 * Real data via /aweni/api/v1/activity/feed. Falls back to skeleton (NOT mock data) on
 * 401/404/network errors so a reviewer or guest never sees fictional activity.
 */

import type { Widget, WidgetContext } from '../types';

interface ActivityEvent {
  id: string;
  app: string;
  action: string;
  label: string;
  timestamp: string;
}

const ACTIVITY_ICONS: Record<string, string> = {
  game_played: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/></svg>',
  card_reviewed: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 11h6"/></svg>',
  expense_added: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>',
  order_placed: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/></svg>',
  login: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="7" r="4"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></svg>',
};

const ACTIVITY_COLORS: Record<string, string> = {
  game_played: '#3B82F6',
  card_reviewed: '#F59E0B',
  expense_added: '#8B5CF6',
  order_placed: '#14B8A6',
  login: '#64748B',
};

function formatRelativeTime(timestamp: string, t: (k: string, p?: Record<string, string | number>) => string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return t('time_just_now');
  if (diffMin < 60) return t('time_minutes_ago', { count: diffMin });
  if (diffHr < 24) return t('time_hours_ago', { count: diffHr });
  return t('time_days_ago', { count: diffDay });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? value as Record<string, unknown> : null;
}

function parseActivityFeed(payload: unknown): ActivityEvent[] {
  const root = asRecord(payload);
  const list = Array.isArray(root?.data) ? root.data
    : Array.isArray(root?.events) ? root.events
    : Array.isArray(root?.feed) ? root.feed
    : Array.isArray(payload) ? payload : [];
  return (list as unknown[]).map((raw, i) => {
    const r = asRecord(raw) ?? {};
    return {
      id: String(r.id ?? r.event_id ?? i),
      app: typeof r.app === 'string' ? r.app : '',
      action: typeof r.action === 'string' ? r.action : 'login',
      label: typeof r.label === 'string' ? r.label : '',
      timestamp: typeof r.timestamp === 'string' ? r.timestamp
        : typeof r.created_at === 'string' ? r.created_at
        : new Date().toISOString(),
    };
  });
}

function getErrorStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null;
  const e = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof e.status === 'number') return e.status;
  if (typeof e.response?.status === 'number') return e.response.status;
  return null;
}

function isExpectedAuthOrOfflineError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 401 || status === 404) return true;
  if (error instanceof TypeError) return true;
  const msg = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : String(error ?? '');
  return /network|failed to fetch|load failed|offline|timeout|http\s*401|http\s*404/i.test(msg);
}

export const ActivityFeed: Widget = {
  id: 'activity-feed',
  label: { en: 'Activity Feed', fr: 'Activite recente', ar: 'النشاط الأخير', es: 'Actividad reciente', de: 'Letzte Aktivitaet' },
  icon: '📋',
  defaultVisible: true,
  defaultOrder: 10,
  defaultSize: 'expanded',

  render(container: HTMLElement, _size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const labelKeys: Record<string, string> = {
      game_played: 'activity_game_played',
      card_reviewed: 'activity_card_reviewed',
      expense_added: 'activity_expense_added',
      order_placed: 'activity_order_placed',
      login: 'activity_login',
    };

    const renderSkeleton = () => {
      container.innerHTML = `
        <div class="flex items-center justify-between mb-3">
          <div class="h-3 w-32 rounded skeleton-pulse" style="background:var(--bg-secondary);"></div>
        </div>
        <div class="relative" style="padding-inline-start:20px;">
          <div class="absolute" style="top:8px;bottom:8px;${ctx.isRtl ? 'right' : 'left'}:7px;width:2px;background:var(--border);"></div>
          ${[0, 1, 2].map(() => `
            <div class="relative flex items-start gap-3 mb-3">
              <div class="absolute" style="${ctx.isRtl ? 'right' : 'left'}:-20px;top:4px;width:14px;height:14px;border-radius:50%;background:var(--bg-secondary);border:2px solid var(--bg-primary);"></div>
              <div class="flex-1 rounded-xl p-3 skeleton-pulse" style="background:var(--bg-secondary);height:44px;"></div>
            </div>`).join('')}
        </div>`;
    };

    const renderEmpty = () => {
      container.innerHTML = `
        <div class="rounded-2xl p-6 text-center" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <p class="text-sm" style="color:var(--text-secondary);">${ctx.t('home_activity_empty')}</p>
        </div>`;
    };

    const renderEvents = (events: ActivityEvent[]) => {
      container.innerHTML = `
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-sm font-semibold uppercase tracking-wide" style="color:var(--text-secondary);">${ctx.t('home_recent_activity')}</h2>
        </div>
        <div class="relative" style="padding-inline-start:20px;">
          <div class="absolute" style="top:8px;bottom:8px;${ctx.isRtl ? 'right' : 'left'}:7px;width:2px;background:var(--border);"></div>
          ${events.slice(0, 5).map((event, idx) => {
            const color = ACTIVITY_COLORS[event.action] || '#64748B';
            const icon = ACTIVITY_ICONS[event.action] || ACTIVITY_ICONS.login;
            const key = labelKeys[event.action] || 'activity_default';
            return `
              <div class="relative flex items-start gap-3 mb-3" style="animation-delay:${idx * 60}ms;">
                <div class="absolute flex items-center justify-center" style="${ctx.isRtl ? 'right' : 'left'}:-20px;top:4px;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid var(--bg-primary);"></div>
                <div class="flex-1 rounded-xl p-3" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
                  <div class="flex items-center gap-2 mb-1">
                    <span style="color:${color};">${icon}</span>
                    <p class="text-sm font-medium" style="color:var(--text-primary);">${ctx.t(key, { label: event.label, action: event.action })}</p>
                  </div>
                  <p class="text-xs" style="color:var(--text-secondary);">${formatRelativeTime(event.timestamp, ctx.t)}</p>
                </div>
              </div>`;
          }).join('')}
        </div>`;
    };

    renderSkeleton();

    void (async () => {
      try {
        const token = localStorage.getItem('learnature_token');
        const resp = await fetch('/aweni/api/v1/activity/feed?limit=10', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          cache: 'no-store',
        });
        if (!resp.ok) {
          if (resp.status === 401 || resp.status === 404) {
            renderEmpty();
            return;
          }
          throw Object.assign(new Error(`HTTP ${resp.status}`), { status: resp.status });
        }
        const payload = await resp.json();
        const events = parseActivityFeed(payload);
        if (events.length === 0) {
          renderEmpty();
          return;
        }
        renderEvents(events);
      } catch (error: unknown) {
        if (isExpectedAuthOrOfflineError(error)) {
          renderEmpty();
          return;
        }
        console.warn('[ActivityFeed] unexpected feed error:', error);
        renderEmpty();
      }
    })();
  },
};
